import type { TFunction } from 'i18next';
import api from '../../services/api';
import { asArray, asOptionalString, asRecord, asString } from '../../dto/common';
import { normalizeUnitCode } from '../../utils/units';
import { submitWithOperationalException } from '../operationalExceptions/operationalExceptionFlow';

type OperationalExceptionModal = Parameters<typeof submitWithOperationalException>[2];

/**
 * „Wystaw WZ” window — reads and the create command
 * (backend `GET /wz/issue-preview/:orderId`, `POST /wz`;
 * decision 2026-10-04, points 1–3). The release itself is
 * `wzReleaseFlow.releaseWZDocument`, shared with every other WZ entry point.
 */

export interface WZIssueBatch {
  inventoryBatchId: string;
  pzDocumentId: string | null;
  pzNumber: string | null;
  receiptDate: string | null;
  /** What this order may take from the batch, in the line unit. */
  available: number;
  /** Whole batch left, in the line unit. */
  remainingQuantity: number;
  /** PLN per line unit incl. capitalized freight; undefined when hidden for the role. */
  unitCostPLN?: number | null;
  /** Cost left in the batch (PLN); undefined when hidden, null when unknown. */
  remainingCostPLN?: number | null;
  /** The whole batch left as a quantity set (pcs, m², m³): the release prices a take in the first shared unit. */
  remainingSet?: WZQuantitySet | null;
  /** false: the receipt has no approved purchase invoice — the release warns (reason required). */
  purchaseInvoiceApproved?: boolean;
}

export type WZQuantitySet = Partial<Record<'pcs' | 'm2' | 'm3', number | null>>;

export interface WZIssueSlot {
  inventoryBatchId: string;
  quantity: number;
}

export interface WZIssueLine {
  sourceOrderProductId: string | null;
  productVariantId: string | null;
  productName: string;
  productCode: string | null;
  unit: string;
  remaining: number;
  onStock: number;
  defaultQuantity: number;
  conversionUnit: string | null;
  width: number | null;
  height: number | null;
  length: number | null;
  thickness: number | null;
  batches: WZIssueBatch[];
  fifoSlots: WZIssueSlot[];
  /** What is left to issue as a quantity set (pcs, m², m³), as the WZ line will carry it. */
  quantitySet?: WZQuantitySet | null;
}

export interface WZIssuePreview {
  orderId: string;
  orderHasProducts: boolean;
  deliveryMode: string | null;
  recipientName: string | null;
  /** Customer seat address (customer card). */
  recipientAddress: string | null;
  /** Prefill: order „Adres dostawy” → customer seat. */
  deliveryAddress: string | null;
  lines: WZIssueLine[];
}

const quantity = (value: unknown) => Math.max(0, Number(value) || 0);
// Missing is not zero (unknown cost, no dimension).
const optionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

function normalizeQuantitySet(raw: unknown): WZQuantitySet | null {
  const record = asRecord(raw);
  if (!record) return null;
  return { pcs: optionalNumber(record.pcs), m2: optionalNumber(record.m2), m3: optionalNumber(record.m3) };
}

function normalizeBatch(raw: unknown): WZIssueBatch | null {
  const record = asRecord(raw);
  const id = asOptionalString(record?.inventoryBatchId);
  if (!record || !id) return null;
  const batch: WZIssueBatch = {
    inventoryBatchId: id,
    pzDocumentId: asOptionalString(record.pzDocumentId) ?? null,
    pzNumber: asOptionalString(record.pzNumber) ?? null,
    receiptDate: asOptionalString(record.receiptDate) ?? null,
    available: quantity(record.available),
    remainingQuantity: quantity(record.remainingQuantity),
  };
  // Redacted for roles without WZ cost access: absent, not zero.
  if (Object.prototype.hasOwnProperty.call(record, 'unitCostPLN')) {
    batch.unitCostPLN = optionalNumber(record.unitCostPLN);
  }
  if (Object.prototype.hasOwnProperty.call(record, 'remainingCostPLN')) {
    batch.remainingCostPLN = optionalNumber(record.remainingCostPLN);
  }
  const set = normalizeQuantitySet(record.remainingSet);
  if (set) batch.remainingSet = set;
  if (typeof record.purchaseInvoiceApproved === 'boolean') batch.purchaseInvoiceApproved = record.purchaseInvoiceApproved;
  return batch;
}

function normalizeLine(raw: unknown): WZIssueLine | null {
  const record = asRecord(raw);
  if (!record) return null;
  return {
    sourceOrderProductId: asOptionalString(record.sourceOrderProductId) ?? null,
    productVariantId: asOptionalString(record.productVariantId) ?? null,
    productName: asString(record.productName),
    productCode: asOptionalString(record.productCode) ?? null,
    unit: normalizeUnitCode(asOptionalString(record.unit)) || 'pcs',
    remaining: quantity(record.remaining),
    onStock: quantity(record.onStock),
    defaultQuantity: quantity(record.defaultQuantity),
    conversionUnit: asOptionalString(record.conversionUnit) ?? null,
    width: optionalNumber(record.width),
    height: optionalNumber(record.height),
    length: optionalNumber(record.length),
    thickness: optionalNumber(record.thickness),
    batches: asArray(record.batches, normalizeBatch).filter((batch): batch is WZIssueBatch => batch !== null),
    fifoSlots: asArray(record.fifoSlots, (slot) => {
      const row = asRecord(slot);
      const id = asOptionalString(row?.inventoryBatchId);
      return id ? { inventoryBatchId: id, quantity: quantity(row?.quantity) } : null;
    }).filter((slot): slot is WZIssueSlot => slot !== null),
    quantitySet: normalizeQuantitySet(record.quantitySet),
  };
}

export function normalizeWZIssuePreview(raw: unknown): WZIssuePreview {
  const record = asRecord(raw);
  return {
    orderId: asString(record?.orderId),
    orderHasProducts: record?.orderHasProducts === true,
    deliveryMode: asOptionalString(record?.deliveryMode) ?? null,
    recipientName: asOptionalString(record?.recipientName) ?? null,
    recipientAddress: asOptionalString(record?.recipientAddress) ?? null,
    deliveryAddress: asOptionalString(record?.deliveryAddress) ?? null,
    lines: asArray(record?.lines, normalizeLine).filter((line): line is WZIssueLine => line !== null),
  };
}

export async function fetchWZIssuePreview(orderId: string): Promise<WZIssuePreview> {
  const response = await api.get(`/wz/issue-preview/${orderId}`);
  return normalizeWZIssuePreview(response.data);
}

export interface WZIssueLineInput {
  line: WZIssueLine;
  quantity: number;
  /** Picks of „Wybiorę partie”; omitted for FIFO. */
  picks?: { inventoryBatchId: string; quantity: number }[];
}

/**
 * POST /wz body: lines from the order (buyer comes from the customer card on
 * the server), the delivery address typed in the window (empty → the server
 * falls back to the order address, then the customer seat), no sale prices —
 * the empty sale pair is reconstructed from the order line server-side.
 */
export function buildWZIssuePayload({ orderId, issueDate, deliveryAddress, lines }: {
  orderId: string;
  issueDate: string;
  deliveryAddress?: string | null;
  lines: WZIssueLineInput[];
}) {
  const typedDeliveryAddress = (deliveryAddress || '').trim();
  return {
    orderId,
    issueDate,
    ...(typedDeliveryAddress ? { deliveryAddress: typedDeliveryAddress } : {}),
    releaseDate: issueDate,
    // No receipt date at issue (owner decision 2026-10-22, PW.4): „Data
    // dostarczenia” stays empty until the goods are actually received; it is
    // entered later on the WZ card.
    items: lines
      .filter((entry) => entry.quantity > 0)
      .map(({ line, quantity: lineQuantity, picks }) => ({
        productVariantId: line.productVariantId,
        sourceOrderProductId: line.sourceOrderProductId,
        productName: line.productName,
        productCode: line.productCode || undefined,
        quantity: lineQuantity,
        unit: line.unit,
        conversionUnit: line.conversionUnit,
        width: line.width ?? undefined,
        height: line.height ?? undefined,
        length: line.length ?? undefined,
        thickness: line.thickness ?? undefined,
        saleQuantity: null,
        saleUnit: null,
        ...(picks && picks.length > 0
          ? { sourceMode: 'BATCHES', batchPicks: picks }
          : { sourceMode: 'FIFO' }),
      })),
  };
}

export const WZ_BATCHES_NOT_FIFO_CODE = 'WZ_BATCHES_NOT_FIFO';
/**
 * Release warnings about goods without an approved purchase invoice (WP12
 * allow-with-confirmation). The „Wystaw WZ” window shows them up front with
 * its own reason field; the release reuses that reason instead of a second
 * dialog. Any other warning still asks in the shared dialog.
 */
export const WZ_PURCHASE_INVOICE_WARNING_CODES = ['WZ_MISSING_PURCHASE_INVOICE', 'WZ_BEFORE_PURCHASE_INVOICE', 'WZ_PENDING_STOCK_ISSUE'];

/**
 * Create the WZ. The not-FIFO warning was shown in the window and its reason
 * typed there, so it is confirmed without a second dialog; any other warning
 * (e.g. another product variant than the order) still asks.
 * Returns null when the operator cancelled a confirmation.
 */
export async function createWZForIssue(
  payload: ReturnType<typeof buildWZIssuePayload>,
  { modal, t, reason }: { modal: OperationalExceptionModal; t: TFunction; reason?: string },
): Promise<{ id?: string; wzNumber?: string } | null> {
  const response = await submitWithOperationalException(
    (body) => api.post('/wz', body),
    payload as unknown as Record<string, unknown>,
    modal,
    t,
    reason ? { presetReason: { codes: [WZ_BATCHES_NOT_FIFO_CODE], reason } } : undefined,
  );
  return response ? response.data : null;
}
